/**
 * Staff auth store for cookie-based federated admin authentication.
 */

import { defineStore } from 'pinia'
import { useStaffApi } from '../composables/useStaffApi'
import type { StaffAuthState, StaffPermission } from './types/staff.types'
import { setSentryUserId, clearSentryUser } from '@/sentry'

function createInitialState(): StaffAuthState {
  return {
    session: null,
    initialized: false,
    loading: {
      init: false,
      logout: false,
    },
    error: {
      init: '',
      logout: '',
    },
  }
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'An unexpected error occurred'
}

function extractErrorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code
    if (typeof code === 'string' && code.length > 0) {
      return code
    }
  }
  return 'SYSTEM_ERROR'
}

export const useStaffAuthStore = defineStore('staffAuth', {
  state: (): StaffAuthState => createInitialState(),

  getters: {
    isAuthenticated(state): boolean {
      return state.session !== null
    },

    currentSlug(state): string | null {
      return state.session?.slug ?? null
    },

    currentStoreId(state): string | null {
      return state.session?.storeId ?? null
    },

    currentStoreName(state): string | null {
      return state.session?.storeName ?? null
    },

    currentStoreType(state): string | null {
      return state.session?.storeType ?? null
    },

    isPharmacyStore(state): boolean {
      return (state.session?.storeType ?? '').toUpperCase() === 'PHARMACY'
    },

    staffUsername(state): string | null {
      return state.session?.lytningUsername ?? null
    },

    staffDisplayName(state): string | null {
      return state.session?.lytningFullName || state.session?.lytningUsername || null
    },

    staffRole(state): string | null {
      return state.session?.role ?? null
    },

    staffPermissions(state): string[] {
      return state.session?.permissions ?? []
    },

    isInitialized(state): boolean {
      return state.initialized
    },

    isLoading(state): boolean {
      return state.loading.init || state.loading.logout
    },
  },

  actions: {
    async initialize(bootstrapToken?: string): Promise<string | null> {
      const hasBootstrapToken =
        typeof bootstrapToken === 'string' && bootstrapToken.trim().length > 0

      if (this.initialized && !hasBootstrapToken) {
        return this.session ? null : 'NO_SESSION'
      }

      this.loading.init = true
      this.error.init = ''

      try {
        const staffApi = useStaffApi()
        const session = hasBootstrapToken
          ? await staffApi.exchangeToken(bootstrapToken!.trim())
          : await staffApi.getSession()

        if (session) {
          this.session = session
          this.initialized = true
          setSentryUserId(session.lytningUserId)
          return null
        }

        this.session = null
        clearSentryUser()
        this.initialized = true
        return 'NO_SESSION'
      } catch (error: unknown) {
        console.error('Failed to initialize staff session:', error)
        this.error.init = extractErrorMessage(error)
        this.session = null
        clearSentryUser()
        this.initialized = true
        return extractErrorCode(error)
      } finally {
        this.loading.init = false
      }
    },

    hasPermission(permission: StaffPermission | string): boolean {
      return this.session?.permissions?.includes(permission) ?? false
    },

    hasAnyPermission(...permissions: (StaffPermission | string)[]): boolean {
      if (!this.session?.permissions) return false
      return permissions.some((permission) => this.session!.permissions.includes(permission))
    },

    hasAllPermissions(...permissions: (StaffPermission | string)[]): boolean {
      if (!this.session?.permissions) return permissions.length === 0
      return permissions.every((permission) => this.session!.permissions.includes(permission))
    },

    canAccessStore(storeSlug: string): boolean {
      return this.session?.slug === storeSlug
    },

    async logout(): Promise<void> {
      this.loading.logout = true
      this.error.logout = ''

      try {
        const staffApi = useStaffApi()
        await staffApi.logout()
      } catch (error: unknown) {
        console.warn('Staff logout API call failed:', error)
      } finally {
        this.session = null
        clearSentryUser()
        this.loading.logout = false
      }
    },

    reset(): void {
      this.$reset()
      clearSentryUser()
    },

    async refreshSession(): Promise<void> {
      if (!this.session) return

      try {
        const staffApi = useStaffApi()
        const session = await staffApi.getSession()
        if (session) {
          this.session = session
          setSentryUserId(session.lytningUserId)
        } else {
          this.session = null
          clearSentryUser()
        }
      } catch (error: unknown) {
        console.error('Failed to refresh staff session:', error)
      }
    },
  },
})

export default useStaffAuthStore
